<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { GenericField } from '@Types';
import type { FieldSchema } from '../../../types';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    binary?: boolean;
    error?: string;
    field?: FieldSchema;
    link?: string;
    linkLabel?: string;
    options?: GenericField[];
  }>(),
  {
    binary: true,
  }
);

const { t } = useI18n();

const model = defineModel<string[] | string>();

const sanitizedOptions = computed(() => props.field?.edit?.options || props.options);
const filteredOptions = computed(() => sanitizedOptions.value?.filter((opt: any) => opt !== null && opt !== undefined));

function isSimpleOption(option: any): option is string {
  return typeof option === 'string' || option === null;
}

function sanitizedOptionValues(option: any, useValue: boolean = false) {
  // prettier-ignore
  return option === null ? t('common.empty') : 
         isSimpleOption(option) ? option : 
         useValue ? option.value : 
         option.name;
}
</script>

<template>
  <div
    v-if="filteredOptions.length"
    class="flex flex-wrap gap-4"
  >
    <div
      v-for="option in filteredOptions"
      class="flex items-center"
      :key="isSimpleOption(option) ? option : `${option.name}_${option.value}`"
    >
      <Checkbox
        v-model="model"
        name="checkbox"
        :inputId="isSimpleOption(option) ? option : `${option.name}_${option.value}`"
        :value="sanitizedOptionValues(option, true)"
      />

      <label
        v-if="!link"
        :class="['ml-2 text-sm', { 'italic text-gray-500': option === null }, { 'af-field-error': !!error }]"
      >
        {{ sanitizedOptionValues(option) }}
      </label>

      <label
        v-if="link && linkLabel"
        :class="['ml-2 flex gap-1 text-sm', { 'af-field-error': !!error }]"
      >
        <span v-if="option">{{ $t(isSimpleOption(option) ? option : option.name) }}</span>

        <a
          class="af-link"
          target="_blank"
          :href="link"
        >
          {{ $t(linkLabel) }}
        </a>
      </label>
    </div>
  </div>

  <p
    v-if="(!filteredOptions || filteredOptions.length === 0) && field?.label"
    class="py-1 text-xs italic text-gray-500"
  >
    {{ $t('components.emptyMessage.missingAnimalField', { field: $t(field?.label || '') }) }}
  </p>
</template>
